import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CreateShipmentRequest } from '../models/sales/create-shipment-request.interface';
import { CompanySalesOrderShipment } from '../models/sales/sales-order-shipment.interface';
import { UpdateShipmentRequest } from '../models/sales/update-shipment-request.interface';

@Injectable({
  providedIn: 'root',
})
export class SalesOrderShipmentService {
  private readonly httpClient = inject(HttpClient);


  public createShipment(
    companyId: string,
    salesOrderId: string,
    request: CreateShipmentRequest,
  ): Observable<CompanySalesOrderShipment> {
    return this.httpClient.post<CompanySalesOrderShipment>(
      this.getShipmentsUrl(companyId, salesOrderId),
      request,
    );
  }

  public getShipments(
    companyId: string,
    salesOrderId: string,
  ): Observable<CompanySalesOrderShipment[]> {
    return this.httpClient.get<CompanySalesOrderShipment[]>(
      this.getShipmentsUrl(companyId, salesOrderId),
    );
  }

  public getShipment(
    companyId: string,
    salesOrderId: string,
    shipmentId: string,
  ): Observable<CompanySalesOrderShipment> {
    return this.httpClient.get<CompanySalesOrderShipment>(
      `${this.getShipmentsUrl(companyId, salesOrderId)}/${shipmentId}`,
    );
  }

  /**
   * Convenience wrapper for the canonical `Packed -> Shipped` transition. Targets the
   * top-level standalone endpoint (`PATCH /api/companies/{companyId}/shipments/{shipmentId}`)
   * because the per-order shipments controller does not expose a status-change verb. The
   * response shape is broadly compatible with `CompanySalesOrderShipment` for the fields the
   * per-order row UI uses (`id`, `number`, `shipmentDate`, `status`, `lines.baseQuantity`,
   * `lines.salesOrderLineId`), so we type the observable as such for caller convenience.
   */
  public markShipped(
    companyId: string,
    shipmentId: string,
  ): Observable<CompanySalesOrderShipment> {
    return this.updateShipment(companyId, shipmentId, { status: 'Shipped' });
  }

  /**
   * Generic PATCH helper used by `markShipped` and any future amend-the-date flow.
   * Backend rejects unsupported transitions with a structured `SHIPMENT_INVALID_STATUS`
   * envelope; callers map that to an i18n key.
   */
  public updateShipment(
    companyId: string,
    shipmentId: string,
    body: UpdateShipmentRequest,
  ): Observable<CompanySalesOrderShipment> {
    return this.httpClient.patch<CompanySalesOrderShipment>(
      this.getStandaloneShipmentUrl(companyId, shipmentId),
      body,
    );
  }

  private getShipmentsUrl(companyId: string, salesOrderId: string): string {
    return `${environment.apiUrl}/companies/${companyId}/sales-orders/${salesOrderId}/shipments`;
  }

  private getStandaloneShipmentUrl(companyId: string, shipmentId: string): string {
    return `${environment.apiUrl}/companies/${companyId}/shipments/${shipmentId}`;
  }
}
